import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiArrowLeft } from 'react-icons/fi';
import TaskModal from '../../components/TaskModal';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../services/api';

const ProjectTasksPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [activeTasks, setActiveTasks] = useState([]);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentTask, setCurrentTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [projectMembers, setProjectMembers] = useState([]);
  const [showDescModal, setShowDescModal] = useState(false);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchClients();
  }, []);

  useEffect(() => {
    if (projectId) {
      fetchProjectTasks(projectId);
      fetchProjectDetail(projectId);
      fetchProjectMembers(projectId);
    } else {
      fetchAllTasks();
      setProjectMembers([]);
    }
  }, [projectId]);

  const fetchProjectMembers = async (id) => {
    try {
      const response = await api.get(`/projects/${id}/members`);
      setProjectMembers(response.data.members || []);
    } catch (err) {
      console.error('Error fetching project members:', err);
      setProjectMembers([]);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.post('/projects/list', { limit: 1000 });
      const projectList = response.data.projects || response.data;
      setProjects(projectList);
      if (projectId) {
        const project = projectList.find(p => p.id === parseInt(projectId));
        if (project) setSelectedProject(project);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error fetching projects:', err);
      setError('Failed to load projects. Please try again.');
      setLoading(false);
    }
  };

  const fetchProjectDetail = async (id) => {
    try {
      const response = await api.get(`/projects/${id}`);
      setProjectDetail(response.data);
    } catch (err) {
      console.error('Error fetching project detail:', err);
    }
  };

  const fetchProjectTasks = async (projectId) => {
    try {
      setTasksLoading(true);
      setError(null);
      const response = await api.post(`/projects/${projectId}/tasks`);
      let allTasks = response.data.tasks || response.data || [];
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      setActiveTasks(allTasks.filter(t => t.status !== 'completed').sort((a, b) => {
        const pDiff = (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
        if (pDiff !== 0) return pDiff;
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      }));
      setCompletedTasks(allTasks.filter(t => t.status === 'completed'));
      setTasksLoading(false);
    } catch (err) {
      console.error('Error fetching project tasks:', err);
      setActiveTasks([]);
      setCompletedTasks([]);
      if (err.response && err.response.status !== 404) setError('Failed to load tasks. Please try again.');
      setTasksLoading(false);
    }
  };

  const fetchAllTasks = async () => {
    try {
      setTasksLoading(true);
      setError(null);
      
      const response = await api.get('/admin/tasks');
      let allTasks = [];
      
      if (response.data.tasks) {
        allTasks = response.data.tasks;
      } else {
        allTasks = response.data || [];
      }
      
      setActiveTasks(allTasks.filter(task => task.status !== 'completed'));
      setCompletedTasks(allTasks.filter(task => task.status === 'completed'));
      setTasksLoading(false);
    } catch (err) {
      console.error('Error fetching all tasks:', err);
      setActiveTasks([]);
      setCompletedTasks([]);
      if (err.response && err.response.status !== 404) {
        setError('Failed to load tasks. Please try again.');
      }
      setTasksLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users', { params: { limit: 1000 } });
      setUsers(response.data.users || response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get('/clients');
      setClients(response.data);
    } catch (err) {
      console.error('Error fetching clients:', err);
    }
  };

  const handleCreateTask = () => {
    setCurrentTask(null);
    setShowTaskModal(true);
  };

  const handleEditTask = (task) => {
    setCurrentTask(task);
    setShowTaskModal(true);
  };

  const handleDeleteTask = (task) => {
    setCurrentTask(task);
    setShowDeleteModal(true);
  };

  const handleSaveTask = async (taskData) => {
    try {
      if (selectedProject && !taskData.project_id) {
        taskData.project_id = selectedProject.id;
      }
      
      let response;
      if (currentTask?.id) {
        response = await api.put(`/admin/tasks/${currentTask.id}`, taskData);
      } else {
        response = await api.post('/admin/tasks', taskData);
      }
      
      setShowTaskModal(false);
      setCurrentTask(null);
      
      if (projectId && selectedProject) {
        fetchProjectTasks(projectId);
      } else if (selectedProject) {
        fetchProjectTasks(selectedProject.id);
      } else {
        fetchAllTasks();
      }
    } catch (err) {
      console.error('Error saving task:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
      alert(`Failed to save task: ${errorMessage}`);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/admin/tasks/${currentTask.id}`);
      setShowDeleteModal(false);
      
      if (projectId && selectedProject) {
        fetchProjectTasks(projectId);
      } else if (selectedProject) {
        fetchProjectTasks(selectedProject.id);
      } else {
        fetchAllTasks();
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      alert('Failed to delete task. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No due date';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      todo: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800',
      testing: 'bg-purple-100 text-purple-800',
      client_review: 'bg-orange-100 text-orange-800',
      completed: 'bg-green-100 text-green-800'
    };
    
    const statusLabels = {
      todo: 'To Do',
      in_progress: 'In Progress',
      testing: 'Testing',
      client_review: 'Client Review',
      completed: 'Completed'
    };
    
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const getPriorityBadge = (priority, isCompleted = false) => {
    if (isCompleted) {
      return (
        <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
          {priority.charAt(0).toUpperCase() + priority.slice(1)}
        </span>
      );
    }
    const priorityClasses = {
      low: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      high: 'bg-red-500 text-white animate-pulse'
    };
    return (
      <span className={`px-2 py-1 text-xs rounded-full ${priorityClasses[priority] || 'bg-gray-100 text-gray-800'}`}>
        {priority.charAt(0).toUpperCase() + priority.slice(1)}
      </span>
    );
  };

  const renderTasksTable = (taskList, isCompleted = false) => {
    if (taskList.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No tasks found.
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Task
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status / Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Due Date
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {taskList.map(task => (
              <tr key={task.id} className={`border-l-4 ${
                isCompleted ? 'border-gray-300 bg-white hover:bg-gray-50' :
                task.priority === 'high' ? 'border-red-500 bg-red-50 hover:bg-red-100' :
                task.priority === 'medium' ? 'border-yellow-400 hover:bg-gray-50' :
                'border-green-400 hover:bg-gray-50'
              }`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => navigate(`/admin/tasks/${task.id}`)}
                    className="text-left hover:bg-gray-50 p-1 -m-1 rounded w-full"
                  >
                    <div className="text-sm font-medium text-blue-600 hover:text-blue-800">{task.title}</div>
                    {task.Project && !selectedProject && (
                      <div className="text-xs text-gray-500 mt-1">
                        Project: {task.Project.name}
                      </div>
                    )}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex flex-col gap-1">
                    {getStatusBadge(task.status)}
                    {getPriorityBadge(task.priority, isCompleted)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(task.due_date)}</div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm text-gray-900">
                    {task.Users && task.Users.length > 0
                      ? task.Users.map((user, i) => <div key={i}>{user.name}</div>)
                      : task.Creator
                        ? `${task.Creator.name} (creator)`
                        : 'Unassigned'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(task.created_at)}</div>
                  <div className="text-xs text-gray-500">{new Date(task.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleEditTask(task)}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <FiEdit2 size={18} title="Edit Task" />
                    </button>
                    <button
                      onClick={() => handleDeleteTask(task)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <FiTrash2 size={18} title="Delete Task" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Project Tasks</h1>
        <div className="text-center py-8">Loading...</div>
      </div>
    );
  }

  if (error && !tasksLoading) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold mb-6">Project Tasks</h1>
        <div className="text-center py-8 text-red-500">{error}</div>
        <button 
          onClick={fetchProjects} 
          className="mx-auto block px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/admin/projects')}
            className="mr-4 p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md flex items-center"
            title="Back to Projects"
          >
            <FiArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-semibold">
            {selectedProject ? `${selectedProject.name} - Tasks` : 'Project Tasks'}
          </h1>
        </div>
        <button
          onClick={handleCreateTask}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center"
        >
          <FiPlus className="mr-2" /> Create Task
        </button>
      </div>

      {projectId ? (
        <div className="flex gap-6">
          {/* Sidebar - Project Description */}
          {projectDetail && (
            <div className="w-64 flex-shrink-0">
              <div className="bg-white shadow-md rounded-lg p-4 sticky top-4">
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">Project Info</h2>
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Name</p>
                  <p className="text-sm font-medium text-gray-900">{projectDetail.name}</p>
                </div>
                {projectDetail.description && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Description</p>
                    <button
                      onClick={() => setShowDescModal(true)}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Click here
                    </button>
                  </div>
                )}
                {projectDetail.Client && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Client</p>
                    <p className="text-sm text-gray-700">{projectDetail.Client.name}</p>
                  </div>
                )}
                <div className="mb-3">
                  <p className="text-xs text-gray-500 mb-1">Status</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    projectDetail.status === 'active' ? 'bg-green-100 text-green-800' :
                    projectDetail.status === 'on_hold' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>{projectDetail.status}</span>
                </div>
                {projectDetail.start_date && (
                  <div className="mb-3">
                    <p className="text-xs text-gray-500 mb-1">Start Date</p>
                    <p className="text-sm text-gray-700">{new Date(projectDetail.start_date).toLocaleDateString()}</p>
                  </div>
                )}
                {projectDetail.end_date && (
                  <div className="mb-2">
                    <p className="text-xs text-gray-500 mb-1">End Date</p>
                    <p className="text-sm text-gray-700">{new Date(projectDetail.end_date).toLocaleDateString()}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Main Content - Tasks */}
          <div className="flex-1 min-w-0">
            {/* Active Tasks Section */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h2 className="text-lg font-medium text-gray-900">Active Tasks ({activeTasks.length})</h2>
              </div>
              {activeTasks.length === 0 && !tasksLoading ? (
                <div className="text-center py-8 text-gray-500">No active tasks found for this project. Click "Create Task" to add a new task.</div>
              ) : tasksLoading ? (
                <div className="text-center py-8 text-gray-500">Loading tasks...</div>
              ) : (
                renderTasksTable(activeTasks)
              )}
            </div>
            {completedTasks.length > 0 && (
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h2 className="text-lg font-medium text-gray-900">Completed Tasks ({completedTasks.length})</h2>
                </div>
                {renderTasksTable(completedTasks, true)}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* All Projects View - Projects List + Tasks */
        <div className="flex flex-col md:flex-row gap-6">
          {/* Projects List */}
          <div className="w-full md:w-1/4">
            <div className="bg-white shadow-md rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h2 className="text-lg font-medium text-gray-900">Projects</h2>
              </div>
              <ul className="divide-y divide-gray-200">
                <li>
                  <button
                    onClick={() => navigate('/admin/project-tasks')}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${!projectId ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                  >
                    All Tasks
                  </button>
                </li>
                {projects.map(project => (
                  <li key={project.id}>
                    <button
                      onClick={() => navigate(`/admin/projects/${project.id}/tasks`)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 ${projectId && parseInt(projectId) === project.id ? 'bg-blue-50 text-blue-700 font-medium' : ''}`}
                    >
                      {project.name}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Tasks List */}
          <div className="w-full md:w-3/4">
            {/* Active Tasks Section */}
            <div className="bg-white shadow-md rounded-lg overflow-hidden mb-6">
              <div className="bg-gray-50 px-4 py-3 border-b">
                <h2 className="text-lg font-medium text-gray-900">
                  Active Tasks ({activeTasks.length})
                </h2>
              </div>
              {activeTasks.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No active tasks found. Click "Create Task" to add a new task.
                </div>
              ) : (
                renderTasksTable(activeTasks)
              )}
            </div>

            {/* Completed Tasks Section */}
            {completedTasks.length > 0 && (
              <div className="bg-white shadow-md rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-4 py-3 border-b">
                  <h2 className="text-lg font-medium text-gray-900">
                    Completed Tasks ({completedTasks.length})
                  </h2>
                </div>
                {renderTasksTable(completedTasks, true)}
              </div>
            )}
          </div>
        </div>
      )}

      {showTaskModal && (
        <TaskModal
          task={currentTask}
          onClose={() => setShowTaskModal(false)}
          onSave={handleSaveTask}
          users={users}
          clients={clients}
          projects={projects}
          selectedProject={selectedProject}
          projectTeam={null}
        />
      )}

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Task"
          message={`Are you sure you want to delete "${currentTask.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}

      {showDescModal && projectDetail?.description && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">{projectDetail.name} — Description</h3>
              <button onClick={() => setShowDescModal(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">&times;</button>
            </div>
            <div className="text-sm text-gray-700 leading-relaxed max-h-[70vh] overflow-y-auto prose prose-sm max-w-none"
              dangerouslySetInnerHTML={{ __html: projectDetail.description }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectTasksPage;